import React, { useState, useEffect } from 'react';
import ReactPixel from 'react-facebook-pixel';

const ValidationAnimation: React.FC<{ isMobile: boolean; onComplete: () => void }> = ({ isMobile, onComplete }) => {
  const [currentStep, setCurrentStep] = React.useState(0);
  
  const validationSteps = [
    'Checking property eligibility...',
    'Verifying rebate availability in your area...',
    'Calculating potential savings...'
  ];
  
  React.useEffect(() => {
    const interval = setInterval(() => {
      setCurrentStep(prev => {
        if (prev < validationSteps.length - 1) {
          return prev + 1;
        } else {
          clearInterval(interval);
          setTimeout(() => {
            onComplete();
          }, 800);
          return prev;
        }
      });
    }, 1500);
    
    return () => clearInterval(interval);
  }, [onComplete, validationSteps.length]);
  
  return (
    <div style={{
      textAlign: 'center',
      padding: '60px 20px',
      minHeight: '450px',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center'
    }}>
      <div style={{
        width: '80px',
        height: '80px',
        border: '4px solid #e2e8f0',
        borderTop: '4px solid #141D46',
        borderRadius: '50%',
        animation: 'spin 1s linear infinite',
        marginBottom: '30px'
      }}></div>
      
      <h3 style={{
        fontSize: isMobile ? '20px' : '24px',
        color: '#333',
        marginBottom: '20px',
        minHeight: '30px'
      }}>
        {validationSteps[currentStep]}
      </h3>
      
      {/* Progress indicators */}
      <div style={{
        display: 'flex',
        gap: '10px',
        marginTop: '20px'
      }}>
        {validationSteps.map((_, index) => (
          <div
            key={index}
            style={{
              width: '8px',
              height: '8px',
              borderRadius: '50%',
              backgroundColor: index <= currentStep ? '#141D46' : '#e2e8f0',
              transition: 'background-color 0.3s'
            }}
          />
        ))}
      </div>
      
      {/* Checkmarks for completed steps */}
      <div style={{
        marginTop: '30px',
        display: 'flex',
        flexDirection: 'column',
        gap: '15px',
        alignItems: 'flex-start',
        minWidth: isMobile ? '280px' : '400px'
      }}>
        {validationSteps.map((step, index) => (
          <div
            key={index}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
              opacity: index <= currentStep ? 1 : 0.3,
              transition: 'opacity 0.5s'
            }}
          >
            <span style={{
              fontSize: '20px',
              color: index < currentStep ? '#00a651' : index === currentStep ? '#141D46' : '#999'
            }}>
              {index < currentStep ? '✓' : index === currentStep ? '⟳' : '○'}
            </span>
            <span style={{
              fontSize: isMobile ? '14px' : '16px',
              color: index <= currentStep ? '#333' : '#999'
            }}>
              {step}
            </span>
          </div>
        ))}
      </div>
      
      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

const ProcessingScreen: React.FC<{ isMobile: boolean; onComplete: () => void }> = ({ isMobile, onComplete }) => {
  const [processingStep, setProcessingStep] = useState(0);
  const [showSuccess, setShowSuccess] = useState(false);
  const [successStep, setSuccessStep] = useState(1);
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [showFinalSuccess, setShowFinalSuccess] = useState(false);
  
  const messages = [
    'Scanning available government incentives',
    'Analysing postcode, income & age',
    'Determining Eligibility'
  ];
  
  React.useEffect(() => {
    if (showSuccess) return;
    
    const interval = setInterval(() => {
      setProcessingStep(prev => {
        if (prev < messages.length - 1) {
          return prev + 1;
        } else {
          clearInterval(interval);
          setTimeout(() => {
            setShowSuccess(true);
          }, 1000);
          return prev;
        }
      });
    }, 2000);
    
    return () => clearInterval(interval);
  }, [messages.length, showSuccess]);
  
  // Show final success screen after form submission
  if (showFinalSuccess) {
    return (
      <div style={{
        textAlign: 'center',
        padding: '40px 20px',
        minHeight: '400px',
        maxWidth: '600px',
        margin: '0 auto'
      }}>
        {/* 100% Complete Progress Bar */}
        <div style={{ marginBottom: '30px' }}>
          <div style={{
            textAlign: 'center',
            marginBottom: '10px',
            fontSize: '16px',
            color: '#00a651',
            fontWeight: 'bold'
          }}>
            Complete - 100%
          </div>
          <div style={{
            width: '100%',
            height: '8px',
            backgroundColor: '#e2e8f0',
            borderRadius: '4px',
            overflow: 'hidden'
          }}>
            <div style={{
              width: '100%',
              height: '100%',
              backgroundColor: '#141D46',
              transition: 'width 1s ease-in-out',
              backgroundImage: 'repeating-linear-gradient(45deg, transparent, transparent 10px, rgba(255,255,255,.2) 10px, rgba(255,255,255,.2) 20px)'
            }}></div>
          </div>
        </div>
        <div style={{
          fontSize: '60px',
          color: '#00a651',
          marginBottom: '20px'
        }}>
          🎉
        </div>
        <h2 style={{
          fontSize: isMobile ? '36px' : '48px',
          fontWeight: 'bold',
          color: '#00a651',
          marginBottom: '20px'
        }}>
          Congratulations!
        </h2>
        <p style={{
          fontSize: isMobile ? '20px' : '24px',
          color: '#333',
          marginBottom: '30px',
          lineHeight: '1.5',
          fontWeight: 'bold'
        }}>
          Your Free Property Report Has Been Generated!
        </p>
        <p style={{
          fontSize: isMobile ? '18px' : '20px',
          color: '#666',
          marginBottom: '30px',
          lineHeight: '1.5'
        }}>
          One of our property investment specialists will be in contact with you shortly to discuss:
        </p>
        <div style={{
          textAlign: 'left',
          maxWidth: '400px',
          margin: '0 auto 30px',
          fontSize: isMobile ? '16px' : '18px',
          color: '#333'
        }}>
          <div style={{ marginBottom: '15px', display: 'flex', alignItems: 'flex-start' }}>
            <span style={{ color: '#00a651', marginRight: '10px', fontSize: '20px' }}>✓</span>
            <span>Your personalised hotspot property report</span>
          </div>
          <div style={{ marginBottom: '15px', display: 'flex', alignItems: 'flex-start' }}>
            <span style={{ color: '#00a651', marginRight: '10px', fontSize: '20px' }}>✓</span>
            <span>Government incentives you qualify for</span>
          </div>
          <div style={{ marginBottom: '15px', display: 'flex', alignItems: 'flex-start' }}>
            <span style={{ color: '#00a651', marginRight: '10px', fontSize: '20px' }}>✓</span>
            <span>Tax strategies to redirect into your property</span>
          </div>
          <div style={{ marginBottom: '15px', display: 'flex', alignItems: 'flex-start' }}>
            <span style={{ color: '#00a651', marginRight: '10px', fontSize: '20px' }}>✓</span>
            <span>Your pathway to property ownership in 2025</span>
          </div>
        </div>
        <div style={{
          padding: '20px',
          backgroundColor: '#f0f8ff',
          borderRadius: '10px',
          marginBottom: '30px'
        }}>
          <p style={{
            fontSize: isMobile ? '16px' : '18px',
            color: '#333',
            margin: 0,
            fontWeight: '600'
          }}>
            📞 Expect a call within 24 hours
          </p>
          <p style={{
            fontSize: isMobile ? '14px' : '16px',
            color: '#666',
            marginTop: '10px'
          }}>
            Please ensure you answer calls from (07) numbers
          </p>
        </div>
        <button
          onClick={() => {
            setShowFinalSuccess(false);
            onComplete();
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = '#2a3d66';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = '#141D46';
          }}
          style={{
            padding: '18px 40px',
            fontSize: '20px',
            fontWeight: 'bold',
            color: '#fff',
            backgroundColor: '#141D46',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
            transition: 'background-color 0.3s'
          }}
        >
          Close
        </button>
      </div>
    );
  }
  
  if (showSuccess) {
    return (
      <div style={{
        textAlign: 'center',
        padding: '40px 20px',
        minHeight: '400px',
        maxWidth: '600px',
        margin: '0 auto'
      }}>
        <h2 style={{
          fontSize: isMobile ? '32px' : '42px',
          fontWeight: 'bold',
          color: '#00a651',
          marginBottom: '20px'
        }}>
          Success!
        </h2>
        <p style={{
          fontSize: isMobile ? '18px' : '22px',
          color: '#333',
          marginBottom: '40px',
          lineHeight: '1.5'
        }}>
          We've mapped out a strategy that works for you.<br/>
          Please confirm your details.
        </p>
        
        <div style={{
          textAlign: 'left',
          marginBottom: '30px'
        }}>
          {successStep === 1 ? (
            <>
              <input
                type="text"
                placeholder="Your first name"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                style={{
                  width: '100%',
                  padding: '15px',
                  fontSize: '16px',
                  marginBottom: '15px',
                  border: '2px solid #e2e8f0',
                  borderRadius: '4px',
                  boxSizing: 'border-box'
                }}
              />
              <input
                type="text"
                placeholder="Your last name"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                style={{
                  width: '100%',
                  padding: '15px',
                  fontSize: '16px',
                  marginBottom: '20px',
                  border: '2px solid #e2e8f0',
                  borderRadius: '4px',
                  boxSizing: 'border-box'
                }}
              />
            </>
          ) : (
            <>
              <input
                type="email"
                placeholder="Your email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                style={{
                  width: '100%',
                  padding: '15px',
                  fontSize: '16px',
                  marginBottom: '15px',
                  border: '2px solid #e2e8f0',
                  borderRadius: '4px',
                  boxSizing: 'border-box'
                }}
              />
              <input
                type="tel"
                placeholder="Your phone"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                style={{
                  width: '100%',
                  padding: '15px',
                  fontSize: '16px',
                  marginBottom: '20px',
                  border: '2px solid #e2e8f0',
                  borderRadius: '4px',
                  boxSizing: 'border-box'
                }}
              />
            </>
          )}
        </div>
        
        <button
          onClick={async () => {
            if (successStep === 1) {
              // Move to step 2 if on step 1
              setSuccessStep(2);
            } else {
              // Submit on step 2
              // Save to localStorage
              localStorage.setItem('fname', firstName);
              localStorage.setItem('lname', lastName);
              localStorage.setItem('email', email);
              localStorage.setItem('phone', phone);
              
              // Get all form data from localStorage
              const formData = {
                firstName,
                lastName,
                email,
                phone,
                livingSituation: localStorage.getItem('livingSituation') || '',
                postcodeOptions: localStorage.getItem('postcodeOptions') || '',
                householdIncome: localStorage.getItem('householdIncome') || '',
                superBalance: localStorage.getItem('superBalance') || '',
                yourAge: localStorage.getItem('yourAge') || '',
                bestDescribes: localStorage.getItem('bestDescribes') || '',
                ownedYourHome: localStorage.getItem('ownedYourHome') || ''
              };
              
              console.log('=== PIPEDRIVE INTEGRATION START ===');
              console.log('📋 Form Data collected:', {
                firstName,
                lastName,
                email,
                phone,
                livingSituation: formData.livingSituation,
                postcode: formData.postcodeOptions,
                householdIncome: formData.householdIncome,
                superBalance: formData.superBalance,
                age: formData.yourAge,
                mainGoal: formData.bestDescribes,
                ownedHome3Years: formData.ownedYourHome
              });
              
              // Create lead in Pipedrive
              console.log('=== 🚀 PIPEDRIVE INTEGRATION START ===');
              console.log('📅 Timestamp:', new Date().toISOString());
              console.log('🌐 Environment:', window.location.href);

              try {
                const apiKey = '74c8e81114eb1ab5e825479acc8739360fcedf47';
                const leadTitle = `${firstName} ${lastName} - Save.OneSolarAustralia.com.au - Lead`;

                console.log('🔑 Using API Key:', apiKey.substring(0, 10) + '...');
                console.log('📝 Lead Title:', leadTitle);
                console.log('👤 User Data:', {
                  firstName,
                  lastName,
                  email,
                  phone
                });

                // Quick API key validation test
                console.log('🧪 Testing API key validity...');
                const testResponse = await fetch('https://oneproperty.pipedrive.com/api/v1/users/me?api_token=' + apiKey);
                if (testResponse.ok) {
                  const testData = await testResponse.json();
                  console.log('✅ API Key is valid! Connected as:', testData.data?.name || 'Unknown user');
                } else {
                  console.error('❌ API Key test failed! Status:', testResponse.status);
                  console.error('This API key may be invalid or expired.');
                }
                
                // Store note content for later use - solar incentives focus
                const noteContent = `
                  Website: save.onesolaraustralia.com.au
                  Property Type: ${formData.livingSituation}
                  Location (Postcode): ${formData.postcodeOptions}
                  Household Income: ${formData.householdIncome}
                  Investment Capacity (Super): ${formData.superBalance}
                  Age: ${formData.yourAge}
                  Solar Goals: ${formData.bestDescribes}
                  Existing Homeowner (3+ years): ${formData.ownedYourHome}
                  Email: ${email}
                  Phone: ${phone}

                  Lead Source: Solar Incentives Landing Page
                  Interest: Government solar rebates and incentives
                `;
                
                // OneSolarAustralia label ID from Pipedrive (green label)
                const oneSolarAustraliaLabelId = '4781efc0-98f6-11f0-800e-07f852cab840';
                
                if (!oneSolarAustraliaLabelId) {
                  console.warn('⚠️ REMINDER: Add OneSolarAustralia label ID to automatically tag leads');
                  console.warn('   1. Go to Pipedrive → Leads → Lead Labels');
                  console.warn('   2. Create "save.onesolaraustralia.com.au" label (green color)');
                  console.warn('   3. Copy the label ID and add it to line 472');
                } else {
                  console.log('🏷️ Using OneSolarAustralia label ID:', oneSolarAustraliaLabelId);
                }
                
                // STEP 1: Create a person
                const personData = {
                  name: `${firstName} ${lastName}`,
                  email: [
                    {
                      value: email,
                      primary: true,
                      label: 'work'
                    }
                  ],
                  phone: [
                    {
                      value: phone,
                      primary: true,
                      label: 'mobile'
                    }
                  ],
                  visible_to: '3' // Everyone can see
                };
                
                console.log('📤 STEP 1: Creating Person...');
                console.log('Person payload:', JSON.stringify(personData, null, 2));

                // Log the full URL for debugging
                const personUrl = 'https://oneproperty.pipedrive.com/api/v1/persons?api_token=' + apiKey;
                console.log('🔗 Person API URL:', personUrl.replace(apiKey, apiKey.substring(0, 10) + '...'));

                const personResponse = await fetch(personUrl, {
                  method: 'POST',
                  headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json'
                  },
                  body: JSON.stringify(personData)
                });
                
                console.log('📥 Person Response Status:', personResponse.status, personResponse.statusText);

                // Check if response is ok
                if (!personResponse.ok) {
                  console.error('❌ Person API call failed with status:', personResponse.status);
                  console.error('Response headers:', personResponse.headers);
                }

                const personResult = await personResponse.json();
                console.log('📥 Person Creation Full Response:', JSON.stringify(personResult, null, 2));

                if (!personResult.success) {
                  console.error('❌ Person creation failed!');
                  console.error('Error:', personResult.error);
                  console.error('Error Info:', personResult.error_info);
                  console.error('Additional Info:', personResult.additional_data);
                }

                if (personResult.success) {
                  console.log('✅ Person created successfully!');
                  console.log('🆔 Person ID:', personResult.data.id);
                  console.log('📧 Person Email:', personResult.data.email);
                  console.log('📱 Person Phone:', personResult.data.phone);
                  
                  // STEP 2: Now create the lead with the person_id and label
                  const leadData = {
                    title: leadTitle,
                    person_id: personResult.data.id, // Link to the person we just created
                    label_ids: oneSolarAustraliaLabelId ? [oneSolarAustraliaLabelId] : [],
                    expected_close_date: null,
                    value: {
                      amount: 0,
                      currency: 'AUD'
                    }
                  };
                  
                  console.log('📤 STEP 2: Creating Lead with Person ID...');
                  console.log('Lead payload:', JSON.stringify(leadData, null, 2));
                  
                  const leadResponse = await fetch('https://oneproperty.pipedrive.com/api/v1/leads?api_token=' + apiKey, {
                    method: 'POST',
                    headers: {
                      'Content-Type': 'application/json',
                      'Accept': 'application/json'
                    },
                    body: JSON.stringify(leadData)
                  });
                  
                  console.log('📥 Lead Response Status:', leadResponse.status, leadResponse.statusText);

                  // Check if response is ok
                  if (!leadResponse.ok) {
                    console.error('❌ Lead API call failed with status:', leadResponse.status);
                    const errorText = await leadResponse.text();
                    console.error('Error response body:', errorText);
                  }

                  const leadResult = await leadResponse.json();
                  console.log('📥 Lead Creation Full Response:', JSON.stringify(leadResult, null, 2));

                  if (!leadResult.success) {
                    console.error('❌ Lead creation failed!');
                    console.error('Error:', leadResult.error);
                    console.error('Error Info:', leadResult.error_info);
                    console.error('Additional Info:', leadResult.additional_data);
                  }

                  if (leadResult.success) {
                    console.log('✅ Lead created successfully!');
                    console.log('🆔 Lead ID:', leadResult.data?.id);
                    console.log('🏷️ Lead Labels:', leadResult.data?.label_ids);
                    console.log('👤 Lead Person ID:', leadResult.data?.person_id);
                    console.log('📌 Lead Title:', leadResult.data?.title);
                    
                    // STEP 3: Add a note to the lead
                    console.log('📤 STEP 3: Adding Note to Lead...');
                    const noteData = {
                      content: noteContent,
                      lead_id: leadResult.data?.id
                    };
                    
                    console.log('Note payload:', JSON.stringify(noteData, null, 2));
                    
                    const noteResponse = await fetch('https://oneproperty.pipedrive.com/api/v1/notes?api_token=' + apiKey, {
                      method: 'POST',
                      headers: {
                        'Content-Type': 'application/json',
                        'Accept': 'application/json'
                      },
                      body: JSON.stringify(noteData)
                    });
                    
                    const noteResult = await noteResponse.json();
                    console.log('📥 Note Creation Response:', JSON.stringify(noteResult, null, 2));
                    
                    if (noteResult.success) {
                      console.log('✅ Note added to lead successfully!');
                    } else {
                      console.warn('⚠️ Failed to add note to lead:', noteResult.error);
                    }
                    
                    console.log('=== PIPEDRIVE INTEGRATION COMPLETE ===');
                    console.log('🎉 Summary: Person created with ID:', personResult.data.id);
                    console.log('🎉 Summary: Lead created with ID:', leadResult.data?.id);

                    // Track conversion with Facebook Pixel
                    ReactPixel.track('Lead', {
                      content_name: 'Solar Incentives Assessment',
                      content_category: 'Solar Energy',
                      value: 0.00,
                      currency: 'AUD'
                    });

                    // Set the success state instead of alert
                    setShowFinalSuccess(true);
                    console.log('✅✅✅ FORM SUBMISSION COMPLETE ✅✅✅');
                    console.log('Check the console logs above for Pipedrive integration details');
                    alert('Form submitted! Check console for Pipedrive logs (F12 → Console tab)');
                  } else {
                    console.error('❌ Failed to create lead in Pipedrive!');
                    console.error('Error details:', leadResult.error);
                    console.error('Error info:', leadResult.error_info);
                    setShowFinalSuccess(true);
                  }
                } else {
                  console.error('❌ Failed to create person in Pipedrive!');
                  console.error('Error details:', personResult.error);
                  console.error('Error info:', personResult.error_info);
                  setShowFinalSuccess(true); // Still show success to user
                }
                
              } catch (error: any) {
                console.error('❌ Exception occurred during Pipedrive API call:');
                console.error('Error type:', error?.name || 'Unknown');
                console.error('Error message:', error?.message || 'Unknown error');
                console.error('Error stack:', error?.stack || 'No stack trace');
                console.error('Full error object:', error);
                setShowFinalSuccess(true); // Still show success to user
              }
              
              console.log('=== END OF PIPEDRIVE INTEGRATION ===');
            }
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = '#2a3d66';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = '#141D46';
          }}
          style={{
            width: '100%',
            padding: '18px',
            fontSize: '20px',
            fontWeight: 'bold',
            color: '#fff',
            backgroundColor: '#141D46',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
            transition: 'background-color 0.3s'
          }}
        >
          {successStep === 1 ? 'Next' : 'Submit'}
        </button>
      </div>
    );
  }
  
  return (
    <>
      <div style={{
        textAlign: 'center',
        padding: '40px 20px',
        minHeight: '400px',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        <h2 style={{
          fontSize: isMobile ? '28px' : '36px',
          fontWeight: 'bold',
          color: '#333',
          marginBottom: '10px'
        }}>
          Processing...Hold Tight
        </h2>
        
        {/* Spinning loader */}
        <div style={{
          width: '80px',
          height: '80px',
          border: '4px solid #e2e8f0',
          borderTop: '4px solid #141D46',
          borderRadius: '50%',
          animation: 'spin 1s linear infinite',
          margin: '30px auto'
        }}></div>
        
        {/* Processing messages */}
        <div style={{
          marginTop: '30px',
          minHeight: '100px'
        }}>
          {messages.map((message, index) => (
            <div 
              key={index}
              style={{
                fontSize: isMobile ? '18px' : '20px',
                color: index <= processingStep ? '#00a651' : '#999',
                margin: '15px 0',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '10px',
                opacity: index <= processingStep ? 1 : 0.3,
                transform: index <= processingStep ? 'scale(1)' : 'scale(0.95)',
                transition: 'all 0.5s ease'
              }}
            >
              {index <= processingStep ? (
                <span style={{
                  display: 'inline-block',
                  width: '24px',
                  height: '24px',
                  backgroundColor: '#141D46',
                  borderRadius: '50%',
                  position: 'relative'
                }}>
                  <span style={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    color: 'white',
                    fontSize: '14px',
                    fontWeight: 'bold'
                  }}>✓</span>
                </span>
              ) : (
                <span style={{
                  display: 'inline-block',
                  width: '24px',
                  height: '24px',
                  border: '2px solid #999',
                  borderRadius: '50%',
                  animation: index === processingStep + 1 ? 'pulse 1.5s infinite' : 'none'
                }}></span>
              )}
              {message}
            </div>
          ))}
        </div>
        
        {/* Progress dots */}
        <div style={{
          display: 'flex',
          gap: '8px',
          marginTop: '40px'
        }}>
          {[0, 1, 2].map(dot => (
            <div
              key={dot}
              style={{
                width: '12px',
                height: '12px',
                borderRadius: '50%',
                backgroundColor: dot <= processingStep ? '#141D46' : '#e2e8f0',
                transition: 'background-color 0.3s ease',
                animation: dot === processingStep ? 'bounce 0.6s infinite' : 'none'
              }}
            ></div>
          ))}
        </div>
      </div>
      
      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        
        @keyframes pulse {
          0%, 100% {
            transform: scale(1);
            opacity: 1;
          }
          50% {
            transform: scale(1.1);
            opacity: 0.7;
          }
        }
        
        @keyframes bounce {
          0%, 100% {
            transform: translateY(0);
          }
          50% {
            transform: translateY(-5px);
          }
        }
      `}</style>
    </>
  );
};

const OneSolarIncentivesLandingPage: React.FC = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [formStep, setFormStep] = useState(0);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [formData, setFormData] = useState({
    livingSituation: '',
    postcodeOptions: '',
    householdIncome: '',
    superBalance: '',
    yourAge: '',
    bestDescribes: '',
    firstName: '',
    email: '',
    phone: ''
  });

  // Initialize Facebook Pixel
  useEffect(() => {
    const options = {
      autoConfig: true, // set pixel's autoConfig. More info: https://developers.facebook.com/docs/facebook-pixel/advanced/
      debug: false, // enable logs
    };
    ReactPixel.init('1430902054645078', undefined, options);
    ReactPixel.pageView(); // For tracking page view
  }, []);

  // Handle resize for responsive
  React.useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);


  const styles = {
    container: {
      fontFamily: '"Open Sans", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
      color: '#2c3e50',
      lineHeight: '1.6',
    },
    header: {
      backgroundColor: '#fff',
      position: 'sticky' as const,
      top: 0,
      zIndex: 1000,
      boxShadow: '0 2px 10px rgba(0,0,0,0.08)',
    },
    topBar: {
      backgroundColor: '#1a365d',
      color: '#fff',
      padding: '10px 0',
      fontSize: '14px',
    },
    topBarContent: {
      maxWidth: '1200px',
      margin: '0 auto',
      padding: '0 20px',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
    },
    nav: {
      maxWidth: '1200px',
      margin: '0 auto',
      padding: '0 20px',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      height: 'auto',
    },
    logo: {
      fontSize: '26px',
      fontWeight: '700',
      color: '#1a365d',
      textDecoration: 'none',
      display: 'flex',
      alignItems: 'center',
      gap: '12px',
      padding: 0,
      margin: 0,
      lineHeight: 0,
    },
    logoImage: {
      width: '180px',
      height: '50px',
      backgroundColor: '#f0f4f8',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      borderRadius: '4px',
      fontSize: '14px',
      color: '#718096',
      border: '1px solid #e2e8f0',
    },
    navMenu: {
      display: 'flex',
      gap: '30px',
      listStyle: 'none',
      margin: 0,
      padding: 0,
      alignItems: 'center',
    },
    navMenuMobile: {
      display: 'none',
    },
    navLink: {
      color: '#333',
      textDecoration: 'none',
      fontSize: '16px',
      fontWeight: '500',
      transition: 'color 0.3s',
      cursor: 'pointer',
    },
    navLinkHover: {
      color: '#2c5282',
    },
    ctaButton: {
      backgroundColor: '#141D46',
      color: '#fff',
      padding: '12px 28px',
      borderRadius: '4px',
      textDecoration: 'none',
      fontWeight: '600',
      fontFamily: "'Open Sans', sans-serif",
      transition: 'all 0.3s',
      border: 'none',
      cursor: 'pointer',
      fontSize: '16px',
      boxShadow: '0 2px 4px rgba(20,29,70,0.2)',
    },
    hamburger: {
      display: 'none',
      flexDirection: 'column' as const,
      gap: '4px',
      background: 'none',
      border: 'none',
      cursor: 'pointer',
      padding: '5px',
    },
    hamburgerLine: {
      width: '25px',
      height: '3px',
      backgroundColor: '#333',
      transition: 'all 0.3s',
    },
    mobileMenu: {
      display: 'none',
      position: 'absolute' as const,
      top: '100%',
      left: 0,
      right: 0,
      backgroundColor: '#fff',
      boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
      padding: '20px',
      flexDirection: 'column' as const,
      gap: '20px',
    },
    mobileMenuOpen: {
      display: 'flex',
    },
    hero: {
      background: 'linear-gradient(135deg, #1a365d 0%, #2c5282 100%)',
      padding: '120px 20px 100px',
      position: 'relative' as const,
      overflow: 'hidden',
    },
    heroBackground: {
      position: 'absolute' as const,
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      opacity: 0.1,
      backgroundImage: 'url("data:image/svg+xml,%3Csvg width="60" height="60" viewBox="0 0 60 60" xmlns="http://www.w3.org/2000/svg"%3E%3Cg fill="none" fill-rule="evenodd"%3E%3Cg fill="%23ffffff" fill-opacity="0.1"%3E%3Cpath d="M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z"/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")',
    },
    heroContent: {
      maxWidth: '1200px',
      margin: '0 auto',
      display: 'grid',
      gridTemplateColumns: '1fr 1fr',
      gap: '60px',
      alignItems: 'center',
      position: 'relative' as const,
      zIndex: 1,
    },
    heroText: {
      color: '#fff',
    },
    heroTitle: {
      fontSize: '52px',
      fontWeight: '700',
      marginBottom: '24px',
      lineHeight: '1.1',
      color: '#fff',
    },
    heroSubtitle: {
      fontSize: '22px',
      marginBottom: '32px',
      opacity: 0.95,
      lineHeight: '1.5',
      color: '#fff',
    },
    heroImage: {
      width: '100%',
      height: '450px',
      backgroundColor: '#fff',
      borderRadius: '8px',
      boxShadow: '0 20px 40px rgba(0,0,0,0.2)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: '16px',
      color: '#718096',
    },
    heroButtons: {
      display: 'flex',
      gap: '20px',
      justifyContent: 'center',
      flexWrap: 'wrap' as const,
    },
    primaryButton: {
      backgroundColor: '#141D46',
      color: '#fff',
      padding: '16px 40px',
      borderRadius: '4px',
      textDecoration: 'none',
      fontWeight: '600',
      fontSize: '18px',
      transition: 'all 0.3s',
      display: 'inline-block',
      border: 'none',
      cursor: 'pointer',
      boxShadow: '0 4px 14px rgba(0,166,81,0.3)',
    },
    secondaryButton: {
      backgroundColor: 'transparent',
      color: '#fff',
      padding: '15px 35px',
      borderRadius: '5px',
      textDecoration: 'none',
      fontWeight: '600',
      fontSize: '18px',
      border: '2px solid #fff',
      transition: 'background-color 0.3s',
      display: 'inline-block',
      cursor: 'pointer',
    },
    section: {
      padding: '80px 20px',
      maxWidth: '1200px',
      margin: '0 auto',
    },
    sectionTitle: {
      fontSize: '42px',
      fontWeight: '700',
      textAlign: 'center' as const,
      marginBottom: '20px',
      color: '#1a365d',
    },
    sectionSubtitle: {
      fontSize: '18px',
      textAlign: 'center' as const,
      marginBottom: '50px',
      color: '#666',
      maxWidth: '700px',
      margin: '0 auto 50px',
    },
    features: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
      gap: '40px',
      marginTop: '40px',
    },
    featureCard: {
      backgroundColor: '#fff',
      padding: '30px',
      borderRadius: '10px',
      boxShadow: '0 5px 15px rgba(0,0,0,0.1)',
      transition: 'transform 0.3s, box-shadow 0.3s',
      cursor: 'pointer',
    },
    featureIcon: {
      width: '70px',
      height: '70px',
      backgroundColor: '#e6f7f0',
      borderRadius: '50%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      marginBottom: '24px',
      fontSize: '30px',
      color: '#00a651',
    },
    featureTitle: {
      fontSize: '24px',
      fontWeight: 'bold',
      marginBottom: '15px',
      color: '#333',
    },
    featureDescription: {
      fontSize: '16px',
      color: '#666',
      lineHeight: '1.6',
    },
    stats: {
      backgroundColor: '#f7fafc',
      padding: '80px 20px',
    },
    statsGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
      gap: '40px',
      maxWidth: '1000px',
      margin: '0 auto',
    },
    statCard: {
      textAlign: 'center' as const,
    },
    statNumber: {
      fontSize: '48px',
      fontWeight: 'bold',
      color: '#2c5282',
      marginBottom: '10px',
    },
    statLabel: {
      fontSize: '18px',
      color: '#666',
    },
    testimonials: {
      backgroundColor: '#fff',
      padding: '80px 20px',
    },
    testimonialsGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(350px, 1fr))',
      gap: '30px',
      maxWidth: '1200px',
      margin: '0 auto',
    },
    testimonialCard: {
      backgroundColor: '#f7fafc',
      padding: '30px',
      borderRadius: '10px',
      position: 'relative' as const,
    },
    testimonialQuote: {
      fontSize: '16px',
      fontStyle: 'italic',
      marginBottom: '20px',
      color: '#555',
      lineHeight: '1.6',
    },
    testimonialAuthor: {
      fontSize: '14px',
      fontWeight: 'bold',
      color: '#2c5282',
    },
    testimonialRole: {
      fontSize: '14px',
      color: '#666',
    },
    quoteIcon: {
      position: 'absolute' as const,
      top: '15px',
      left: '15px',
      fontSize: '30px',
      color: '#cbd5e0',
    },
    process: {
      backgroundColor: '#f7fafc',
      padding: '80px 20px',
    },
    processSteps: {
      display: 'grid',
      gridTemplateColumns: 'repeat(5, 1fr)',
      gap: '30px',
      maxWidth: '1200px',
      margin: '0 auto',
    },
    processStep: {
      textAlign: 'center' as const,
      position: 'relative' as const,
    },
    stepNumber: {
      width: '60px',
      height: '60px',
      backgroundColor: '#141D46',
      borderRadius: '50%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      margin: '0 auto 20px',
      fontSize: '24px',
      fontWeight: 'bold',
      color: '#fff',
    },
    stepTitle: {
      fontSize: '20px',
      fontWeight: 'bold',
      marginBottom: '10px',
      color: '#333',
    },
    stepDescription: {
      fontSize: '16px',
      color: '#666',
      lineHeight: '1.6',
    },
    cta: {
      background: 'linear-gradient(135deg, #00a651 0%, #008541 100%)',
      padding: '80px 20px',
      textAlign: 'center' as const,
      color: '#fff',
    },
    ctaTitle: {
      fontSize: '36px',
      fontWeight: 'bold',
      marginBottom: '20px',
    },
    ctaDescription: {
      fontSize: '18px',
      marginBottom: '30px',
      maxWidth: '600px',
      margin: '0 auto 30px',
    },
    footer: {
      backgroundColor: '#2d3748',
      color: '#cbd5e0',
      padding: '60px 20px 30px',
    },
    footerContent: {
      maxWidth: '1200px',
      margin: '0 auto',
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
      gap: '40px',
      marginBottom: '40px',
    },
    footerSection: {
      display: 'flex',
      flexDirection: 'column' as const,
    },
    footerTitle: {
      fontSize: '20px',
      fontWeight: 'bold',
      marginBottom: '20px',
      color: '#fff',
    },
    footerLink: {
      color: '#cbd5e0',
      textDecoration: 'none',
      marginBottom: '10px',
      transition: 'color 0.3s',
      cursor: 'pointer',
    },
    footerText: {
      fontSize: '14px',
      lineHeight: '1.6',
      marginBottom: '10px',
    },
    footerBottom: {
      borderTop: '1px solid #4a5568',
      paddingTop: '30px',
      textAlign: 'center' as const,
      fontSize: '14px',
    },
    socialLinks: {
      display: 'flex',
      gap: '15px',
      marginTop: '20px',
    },
    socialIcon: {
      width: '40px',
      height: '40px',
      backgroundColor: '#4a5568',
      borderRadius: '50%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      color: '#cbd5e0',
      textDecoration: 'none',
      transition: 'background-color 0.3s',
      cursor: 'pointer',
    },
    '@media (max-width: 768px)': {
      heroTitle: {
        fontSize: '32px',
      },
      heroSubtitle: {
        fontSize: '18px',
      },
      heroContent: {
        gridTemplateColumns: '1fr',
      },
      sectionTitle: {
        fontSize: '28px',
      },
      processSteps: {
        gridTemplateColumns: '1fr',
      },
      navMenu: {
        display: 'none',
      },
      hamburger: {
        display: 'flex',
      },
    },
  };

  return (
    <div style={styles.container}>
      {/* Header */}
      <header style={styles.header}>
       
        
        {/* Navigation */}
        <nav style={{...styles.nav, flexWrap: isMobile ? 'wrap' : 'nowrap'}}>
          <a href="/" style={{...styles.logo, display: 'flex', alignItems: 'center', textDecoration: 'none'}}>
            <img src="/assets/onepropertyaustralia/logo.png" alt="One Solar Incentives" style={{ height: isMobile ? '60px' : '80px', display: 'block' }} />
          </a>
          
          {!isMobile && (
            <div style={{ 
              position: 'absolute',
              left: '50%',
              transform: 'translateX(-50%)',
              fontSize: '24px',
              fontWeight: 'bold',
              color: '#141D46',
              fontFamily: "'Open Sans', sans-serif",
              whiteSpace: 'nowrap'
            }}>
              #The Australian Rebate Specialists
            </div>
          )}
          
          <img 
            src="/assets/easypropertyinvestorimages/ssllogo.png" 
            alt="SSL Secured" 
            style={{ 
              height: isMobile ? '30px' : '40px', 
              marginLeft: 'auto',
              marginRight: isMobile ? '10px' : '20px'
            }} 
          />
          
          {/* Start Over Button - Only show when not on first step */}
          {formStep > 0 && (
            <a 
              href="/"
              style={{
                display: 'flex',
              alignItems: 'center',
              gap: '8px',
              padding: '8px 16px',
              marginRight: '20px',
              backgroundColor: 'transparent',
              border: '1px solid #d0d0d0',
              borderRadius: '20px',
              color: '#888',
              textDecoration: 'none',
              fontSize: '14px',
              fontFamily: "'Open Sans', sans-serif",
              transition: 'all 0.3s ease',
              cursor: 'pointer'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = '#f5f5f5';
              e.currentTarget.style.borderColor = '#999';
              e.currentTarget.style.color = '#666';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'transparent';
              e.currentTarget.style.borderColor = '#d0d0d0';
              e.currentTarget.style.color = '#888';
            }}
          >
            {/* Refresh Icon SVG */}
            <svg 
              width="16" 
              height="16" 
              viewBox="0 0 24 24" 
              fill="none" 
              stroke="currentColor" 
              strokeWidth="2" 
              strokeLinecap="round" 
              strokeLinejoin="round"
            >
              <path d="M1 4v6h6"></path>
              <path d="M23 20v-6h-6"></path>
              <path d="M20.49 9A9 9 0 0 0 5.64 5.64L1 10m22 4l-4.64 4.36A9 9 0 0 1 3.51 15"></path>
            </svg>
            <span style={{ display: isMobile ? 'none' : 'inline' }}>Start Over</span>
            </a>
          )}
          
          <button
            style={styles.hamburger}
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          >
            <span style={styles.hamburgerLine}></span>
            <span style={styles.hamburgerLine}></span>
            <span style={styles.hamburgerLine}></span>
          </button>
          
          {isMobile && (
            <div style={{ 
              width: '100%',
              textAlign: 'center',
              fontSize: '16px',
              fontWeight: 'bold',
              color: '#141D46',
              fontFamily: "'Open Sans', sans-serif",
              whiteSpace: 'nowrap',
              marginTop: '10px'
            }}>
              #The Australian Rebate Specialists
            </div>
          )}
        </nav>
        
        {/* Blue bar under nav */}
        <div style={{ 
          width: '100%', 
          height: isMobile ? '40px' : '35px', 
          backgroundColor: '#141D46',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: 'white',
          fontSize: isMobile ? '12px' : '24px',
          fontWeight: '900',
          fontFamily: 'Gilroy',
          padding: isMobile ? '0 10px' : '0',
          overflow: 'hidden',
          whiteSpace: isMobile ? 'normal' : 'nowrap'
        }}>
          <img 
            src="/assets/easypropertyinvestorimages/star.png" 
            alt="5 stars" 
            style={{ 
              height: isMobile ? '14px' : '20px',
              marginRight: isMobile ? '5px' : '10px',
              flexShrink: 0
            }} 
          />
          <span style={{
            textOverflow: 'ellipsis',
            overflow: 'hidden'
          }}>
            Customer Reviews: 5-Star Rated
          </span>
        </div>
        
        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div style={{...styles.mobileMenu, ...styles.mobileMenuOpen}}>
            <a href="#home" style={styles.navLink}>Home</a>
            <a href="#about" style={styles.navLink}>About</a>
            <a href="#services" style={styles.navLink}>Services</a>
            <a href="#resources" style={styles.navLink}>Resources</a>
            <a href="#contact" style={styles.navLink}>Contact</a>
            <button style={styles.ctaButton}>Get Started</button>
          </div>
        )}
      </header>

      {/* Hero Section */}
      <section style={{...styles.hero, background: 'white', backgroundColor: 'white', padding: isMobile ? '40px 15px 60px' : '60px 20px 100px', display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column'}}>
        <h1 style={{ 
          color: '#141D46',
          fontSize: isMobile ? '24px' : '48px',
          fontWeight: '700',
          fontFamily: "'Open Sans', sans-serif",
          margin: 0,
          textAlign: 'center',
          width: '100%'
        }}>
          Australian Homeowners
        </h1>
        <p style={{
          color: '#000000',
          fontSize: isMobile ? '20px' : '48px',
          fontFamily: 'Gilroy',
          fontWeight: '900',
          margin: '10px 0 0 0',
          textAlign: 'center',
          padding: isMobile ? '0 15px' : '0',
          lineHeight: isMobile ? '1.2' : '1.2',
          width: '100%'
        }}>
          See Instantly Which<br/>
          Government Solar &<br/>
          Battery Incentives<br/>
          You're Eligible For
        </p>
        <p style={{
          color: '#000000',
          fontSize: isMobile ? '18px' : '36px',
          fontFamily: 'Gilroy',
          fontWeight: 'bold',
          margin: isMobile ? '15px 0 0 0' : '20px 0 0 0',
          textAlign: 'center',
          padding: isMobile ? '0 15px' : '0',
          width: '100%'
        }}>
          Access Your Strategic Session Today!
        </p>
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          margin: isMobile ? '20px 0' : '30px 0'
        }}>
          <div style={{
            display: 'inline-block',
            border: '3px dotted #141D46',
            borderRadius: '10px',
            padding: '15px 30px'
          }}>
            <p style={{
              color: '#000000',
              fontSize: isMobile ? '20px' : '28px',
              fontFamily: 'Gilroy',
              fontWeight: 'bold',
              margin: 0,
              textAlign: 'center'
            }}>
              Takes Less Than 30-Seconds
            </p>
          </div>
        </div>
        
        {/* Animated Down Arrow */}
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          margin: '20px 0',
          animation: 'bounce 2s infinite'
        }}>
          <style>
            {`
              @keyframes bounce {
                0%, 20%, 50%, 80%, 100% {
                  transform: translateY(0);
                }
                40% {
                  transform: translateY(10px);
                }
                60% {
                  transform: translateY(5px);
                }
              }
            `}
          </style>
          <svg
            width={isMobile ? "80" : "100"}
            height={isMobile ? "80" : "100"}
            viewBox="0 0 24 24"
            fill="none"
            style={{ cursor: 'pointer' }}
            onClick={() => {
              const formElement = document.getElementById('incentive-form');
              if (formElement) {
                formElement.scrollIntoView({ behavior: 'smooth' });
              }
            }}
          >
            <path
              d="M7 10L12 15L17 10"
              stroke="#141D46"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M7 6L12 11L17 6"
              stroke="#141D46"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              opacity="0.5"
            />
          </svg>
        </div>
        
        <div id="incentive-form" style={{
          width: '100%',
          maxWidth: isMobile ? '100%' : '1200px',
          margin: isMobile ? '20px auto 0' : '30px auto 0',
          padding: isMobile ? '0 15px' : '0 20px',
          boxSizing: 'border-box',
          position: 'relative'
        }}>
          {/* Progress Text */}
          <div style={{
            textAlign: 'center',
            marginBottom: '10px',
            fontSize: isMobile ? '18px' : '20px',
            color: '#666',
            fontWeight: '600'
          }}>
            {formStep === 0 ? 'Getting Started...' :
             formStep === 1 ? 'Property Details...' :
             formStep === 2 ? 'Location Information...' :
             formStep === 3 ? 'Financial Profile...' :
             formStep === 4 ? 'Investment Capacity...' :
             formStep === 6 ? 'Personal Details...' :
             formStep === 7 ? 'Goals & Objectives...' :
             formStep === 8 ? 'Finalizing Your Report...' :
             'Progress'} - {Math.min(100, Math.max(10, (formStep + 1) * 12.5)).toFixed(0)}%
          </div>
          <div style={{
            width: '100%',
            minWidth: '300px',
            height: '15px',
            backgroundColor: '#d0d0d0',
            borderRadius: '7px',
            overflow: 'hidden',
            position: 'relative',
            border: '2px solid #141D46',
            boxSizing: 'border-box'
          }}>
            <div 
              role="progressbar" 
              aria-valuenow={Math.min(100, Math.max(10, (formStep + 1) * 12.5))} 
              aria-valuemin={0} 
              aria-valuemax={100} 
              style={{
                width: `${Math.min(100, Math.max(10, (formStep + 1) * 12.5))}%`,
                height: '100%',
                backgroundColor: '#141D46',
                backgroundImage: 'repeating-linear-gradient(45deg, transparent, transparent 10px, rgba(255,255,255,.2) 10px, rgba(255,255,255,.2) 20px)',
                position: 'relative',
                overflow: 'hidden',
                borderRadius: formStep === 8 ? '5px' : '5px 0 0 5px',
                transition: 'width 0.5s ease-in-out'
              }}
            >
              <div style={{
                position: 'absolute',
                top: 0,
                left: 0,
                bottom: 0,
                right: 0,
                backgroundImage: 'linear-gradient(to right, transparent, rgba(255,255,255,0.3), transparent)',
                animation: 'shimmer 2s infinite',
                width: '50%'
              }}></div>
            </div>
          </div>
          
          {/* Orange Arrow positioned to the right of the progress bar - hidden on mobile */}
          {!isMobile && <svg 
            style={{
              position: 'absolute',
              right: '-100px',
              top: '-40px',
              transform: 'translateY(-50%) rotate(60deg)',
              width: '140px',
              height: '140px',
              zIndex: 10
            }}
            viewBox="0 0 60 61"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path 
              d="M44.853,42.952h11.031L38.101,60.731L20.324,42.952h11.16C29.908,22.001,18.995,5.075,4.847,0.96C7.007,0.332,9.243,0,11.529,0C28.812,0,43.037,18.8,44.853,42.952z"
              fill="#f27629"
            />
          </svg>}
          <style>{`
            @keyframes shimmer {
              0% {
                transform: translateX(-100%);
              }
              100% {
                transform: translateX(300%);
              }
            }
          `}</style>
        </div>
        
        {/* Form Wizard Section */}
        <div style={{
          maxWidth: isMobile ? '95%' : '1200px',
          margin: isMobile ? '15px auto 0' : '30px auto 0',
          padding: isMobile ? '20px 15px' : '40px 60px',
          backgroundColor: '#fff',
          borderRadius: '10px',
          position: 'relative',
          minHeight: isMobile ? '600px' : '650px',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center'
        }}>
          <div style={{ width: '100%' }}>
          {/* Trust badges at top of form */}
          {formStep === 0 && (
            <div style={{
              display: 'flex',
              justifyContent: 'center',
              gap: '20px',
              marginBottom: '20px',
              flexWrap: 'wrap'
            }}>
              <span style={{ fontSize: '14px', color: '#666', display: 'flex', alignItems: 'center', gap: '5px' }}>
                <span>🛡️</span> 100% Secure
              </span>
              <span style={{ fontSize: '14px', color: '#666', display: 'flex', alignItems: 'center', gap: '5px' }}>
                <span>🔒</span> Privacy Protected
              </span>
              <span style={{ fontSize: '14px', color: '#666', display: 'flex', alignItems: 'center', gap: '5px' }}>
                <span>🎯</span> Instant Results
              </span>
            </div>
          )}
          {formStep === 0 && (
            <>
              <h2 style={{
                textAlign: 'center',
                fontSize: isMobile ? '28px' : '36px',
                fontWeight: 'bold',
                color: '#141D46',
                marginBottom: isMobile ? '30px' : '40px',
                lineHeight: '1.3'
              }}>
                Do you own or rent your property?
              </h2>
              
              <div style={{
                display: 'grid',
                gridTemplateColumns: isMobile ? '1fr' : 'repeat(2, 1fr)',
                gap: isMobile ? '20px' : '30px',
                marginBottom: isMobile ? '30px' : '40px',
                maxWidth: '600px',
                margin: '0 auto'
              }}>
                <button 
                  onClick={() => {
                    // Save to localStorage with specific key
                    localStorage.setItem('q1-doyouownyourownproperty', 'Yes, I Own');
                    // If they own, go to question 3
                    setFormStep(3);
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = '#2a3d66';
                    e.currentTarget.style.transform = 'scale(1.02)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = '#141D46';
                    e.currentTarget.style.transform = 'scale(1)';
                  }}
                  style={{
                    padding: '25px 20px',
                    fontSize: '20px',
                    fontWeight: '600',
                    color: '#fff',
                    backgroundColor: '#141D46',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    transition: 'all 0.3s',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '15px',
                    boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
                  }}>
                  <span style={{ fontSize: '30px' }}>🔑</span>
                  Yes, I Own
                </button>
                
                <button 
                  onClick={() => {
                    // Save to localStorage with specific key
                    localStorage.setItem('q1-doyouownyourownproperty', 'No, I Rent');
                    // If they rent, go to question 2
                    setFormStep(2);
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = '#2a3d66';
                    e.currentTarget.style.transform = 'scale(1.02)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = '#141D46';
                    e.currentTarget.style.transform = 'scale(1)';
                  }}
                  style={{
                    padding: '25px 20px',
                    fontSize: '20px',
                    fontWeight: '600',
                    color: '#fff',
                    backgroundColor: '#141D46',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    transition: 'all 0.3s',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '15px',
                    boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
                  }}>
                  <span style={{ fontSize: '30px' }}>🏠</span>
                  No, I Rent
                </button>
              </div>
            </>
          )}
          
          {/* Question 2 - For Renters Only */}
          {formStep === 2 && (
            <>
              <h2 style={{
                textAlign: 'center',
                fontSize: isMobile ? '28px' : '36px',
                fontWeight: 'bold',
                color: '#141D46',
                marginBottom: '20px',
                lineHeight: '1.3'
              }}>
                We have Good News & Bad News...
              </h2>
              
              <div style={{
                marginBottom: '30px',
                padding: '0 20px'
              }}>
                <div style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  marginBottom: '20px',
                  gap: '10px'
                }}>
                  <span style={{ fontSize: '24px', color: 'red' }}>❌</span>
                  <p style={{
                    fontSize: isMobile ? '16px' : '18px',
                    color: '#333',
                    margin: 0,
                    textAlign: 'left'
                  }}>
                    <strong>Bad News:</strong> Solar & Battery Rebates are only available to homeowners.
                  </p>
                </div>
                
                <div style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  marginBottom: '20px',
                  gap: '10px'
                }}>
                  <span style={{ fontSize: '24px', color: 'green' }}>✅</span>
                  <p style={{
                    fontSize: isMobile ? '16px' : '18px',
                    color: '#333',
                    margin: 0,
                    textAlign: 'left'
                  }}>
                    <strong>Good News:</strong> If you are renting and are looking to save $$$ on your energy bills instantly,
                  </p>
                </div>
                
                <p style={{
                  fontSize: isMobile ? '16px' : '18px',
                  color: '#333',
                  textAlign: 'center',
                  marginTop: '20px',
                  fontWeight: '500'
                }}>
                  <strong>Click below now</strong> and we'll show you how.
                </p>
              </div>
              
              <div style={{
                display: 'grid',
                gridTemplateColumns: isMobile ? '1fr' : 'repeat(2, 1fr)',
                gap: isMobile ? '20px' : '30px',
                maxWidth: '700px',
                margin: '0 auto'
              }}>
                <button 
                  onClick={() => {
                    // Save to localStorage
                    localStorage.setItem('q2-rentersresponse', 'Actually, I am a Homeowner');
                    // Go to question 3 for homeowners
                    setFormStep(3);
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = '#2a3d66';
                    e.currentTarget.style.transform = 'scale(1.02)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = '#141D46';
                    e.currentTarget.style.transform = 'scale(1)';
                  }}
                  style={{
                    padding: '20px',
                    fontSize: '18px',
                    fontWeight: '600',
                    color: '#fff',
                    backgroundColor: '#141D46',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    transition: 'all 0.3s',
                    boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
                  }}>
                  Actually, I am a Homeowner
                </button>
                
                <button 
                  onClick={() => {
                    // Save to localStorage
                    localStorage.setItem('q2-rentersresponse', 'Lower My Energy Bills Now');
                    // Open in new tab
                    window.open('https://solarincentives.com.au/renters-save-hundreds-on-your-energy-bills-without-solar-in-sight/', '_blank');
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = '#2a3d66';
                    e.currentTarget.style.transform = 'scale(1.02)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = '#141D46';
                    e.currentTarget.style.transform = 'scale(1)';
                  }}
                  style={{
                    padding: '20px',
                    fontSize: '18px',
                    fontWeight: '600',
                    color: '#fff',
                    backgroundColor: '#141D46',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    transition: 'all 0.3s',
                    boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
                  }}>
                  Lower My Energy Bills Now
                </button>
              </div>
            </>
          )}
          
          {/* Question 3 - Solar Panels for Homeowners */}
          {formStep === 3 && (
            <>
              <h2 style={{
                textAlign: 'center',
                fontSize: isMobile ? '28px' : '36px',
                fontWeight: 'bold',
                color: '#141D46',
                marginBottom: '15px',
                lineHeight: '1.3'
              }}>
                Do you have solar panels already installed?
              </h2>
              
              <p style={{
                textAlign: 'center',
                fontSize: isMobile ? '14px' : '16px',
                color: '#666',
                marginBottom: '40px'
              }}>
                To be eligible for the Federal Government Rebate, you must have no existing panels installed
              </p>
              
              <div style={{
                display: 'grid',
                gridTemplateColumns: isMobile ? '1fr' : 'repeat(2, 1fr)',
                gap: isMobile ? '20px' : '30px',
                maxWidth: '600px',
                margin: '0 auto'
              }}>
                <button 
                  onClick={() => {
                    // Save to localStorage
                    localStorage.setItem('q3-solarpanelsinstalled', 'No, I don\'t');
                    // If no panels, go to question 7
                    setFormStep(7);
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = '#2a3d66';
                    e.currentTarget.style.transform = 'scale(1.02)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = '#141D46';
                    e.currentTarget.style.transform = 'scale(1)';
                  }}
                  style={{
                    padding: '25px 20px',
                    fontSize: '20px',
                    fontWeight: '600',
                    color: '#fff',
                    backgroundColor: '#141D46',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    transition: 'all 0.3s',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '15px',
                    boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
                  }}>
                  <span style={{ fontSize: '30px' }}>❌</span>
                  No, I don't
                </button>
                
                <button 
                  onClick={() => {
                    // Save to localStorage
                    localStorage.setItem('q3-solarpanelsinstalled', 'Yes, I do');
                    // If they have panels, go to question 4
                    setFormStep(4);
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = '#2a3d66';
                    e.currentTarget.style.transform = 'scale(1.02)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = '#141D46';
                    e.currentTarget.style.transform = 'scale(1)';
                  }}
                  style={{
                    padding: '25px 20px',
                    fontSize: '20px',
                    fontWeight: '600',
                    color: '#fff',
                    backgroundColor: '#141D46',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    transition: 'all 0.3s',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '15px',
                    boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
                  }}>
                  <span style={{ fontSize: '30px' }}>✅</span>
                  Yes, I do
                </button>
              </div>
            </>
          )}
          
          {/* Question 4 - Is your system giving you the benefits you want? */}
          {formStep === 4 && (
            <>
              <h2 style={{
                textAlign: 'center',
                fontSize: isMobile ? '28px' : '36px',
                fontWeight: 'bold',
                color: '#141D46',
                marginBottom: '40px',
                lineHeight: '1.3'
              }}>
                Is your system giving you the benefits you want?
              </h2>
              
              <div style={{
                display: 'grid',
                gridTemplateColumns: isMobile ? '1fr' : 'repeat(2, 1fr)',
                gap: isMobile ? '20px' : '30px',
                maxWidth: '600px',
                margin: '0 auto'
              }}>
                <button 
                  onClick={() => {
                    localStorage.setItem('q4-systembenefits', 'Not really');
                    // If Not really, go to question 5
                    setFormStep(5);
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = '#2a3d66';
                    e.currentTarget.style.transform = 'scale(1.02)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = '#141D46';
                    e.currentTarget.style.transform = 'scale(1)';
                  }}
                  style={{
                    padding: '25px 20px',
                    fontSize: '20px',
                    fontWeight: '600',
                    color: '#fff',
                    backgroundColor: '#141D46',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    transition: 'all 0.3s',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '15px',
                    boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
                  }}>
                  <span style={{ fontSize: '30px' }}>😕</span>
                  Not really
                </button>
                
                <button 
                  onClick={() => {
                    localStorage.setItem('q4-systembenefits', 'Yes');
                    // If Yes, go to question 6
                    setFormStep(6);
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = '#2a3d66';
                    e.currentTarget.style.transform = 'scale(1.02)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = '#141D46';
                    e.currentTarget.style.transform = 'scale(1)';
                  }}
                  style={{
                    padding: '25px 20px',
                    fontSize: '20px',
                    fontWeight: '600',
                    color: '#fff',
                    backgroundColor: '#141D46',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    transition: 'all 0.3s',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '15px',
                    boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
                  }}>
                  <span style={{ fontSize: '30px' }}>😊</span>
                  Yes
                </button>
              </div>
            </>
          )}
          
          {/* Question 5 - Are you looking to replace your system? */}
          {formStep === 5 && (
            <>
              <h2 style={{
                textAlign: 'center',
                fontSize: isMobile ? '28px' : '36px',
                fontWeight: 'bold',
                color: '#141D46',
                marginBottom: '40px',
                lineHeight: '1.3'
              }}>
                Are you looking to replace your system?
              </h2>
              
              <div style={{
                display: 'grid',
                gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)',
                gap: isMobile ? '20px' : '30px',
                maxWidth: '800px',
                margin: '0 auto'
              }}>
                <button 
                  onClick={() => {
                    localStorage.setItem('q5-replacesystem', 'Yes');
                    // If Yes, go to question 7
                    setFormStep(7);
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = '#2a3d66';
                    e.currentTarget.style.transform = 'scale(1.02)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = '#141D46';
                    e.currentTarget.style.transform = 'scale(1)';
                  }}
                  style={{
                    padding: '25px 20px',
                    fontSize: '20px',
                    fontWeight: '600',
                    color: '#fff',
                    backgroundColor: '#141D46',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    transition: 'all 0.3s',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '15px',
                    boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
                  }}>
                  <span style={{ fontSize: '30px' }}>✅</span>
                  Yes
                </button>
                
                <button 
                  onClick={() => {
                    localStorage.setItem('q5-replacesystem', 'Maybe');
                    // If Maybe, go to question 7
                    setFormStep(7);
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = '#2a3d66';
                    e.currentTarget.style.transform = 'scale(1.02)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = '#141D46';
                    e.currentTarget.style.transform = 'scale(1)';
                  }}
                  style={{
                    padding: '25px 20px',
                    fontSize: '20px',
                    fontWeight: '600',
                    color: '#fff',
                    backgroundColor: '#141D46',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    transition: 'all 0.3s',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '15px',
                    boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
                  }}>
                  <span style={{ fontSize: '30px' }}>❓</span>
                  Maybe
                </button>
                
                <button 
                  onClick={() => {
                    localStorage.setItem('q5-replacesystem', 'No');
                    // If No, go to next step
                    setFormStep(6);
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = '#2a3d66';
                    e.currentTarget.style.transform = 'scale(1.02)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = '#141D46';
                    e.currentTarget.style.transform = 'scale(1)';
                  }}
                  style={{
                    padding: '25px 20px',
                    fontSize: '20px',
                    fontWeight: '600',
                    color: '#fff',
                    backgroundColor: '#141D46',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    transition: 'all 0.3s',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '15px',
                    boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
                  }}>
                  <span style={{ fontSize: '30px' }}>❌</span>
                  No
                </button>
              </div>
            </>
          )}
          
          {/* Question 6 - Are you looking to replace your system? (Same as Q5 but separate) */}
          {formStep === 6 && (
            <>
              <h2 style={{
                textAlign: 'center',
                fontSize: isMobile ? '28px' : '36px',
                fontWeight: 'bold',
                color: '#141D46',
                marginBottom: '40px',
                lineHeight: '1.3'
              }}>
                Are you looking to replace your system?
              </h2>
              
              <div style={{
                display: 'grid',
                gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)',
                gap: isMobile ? '20px' : '30px',
                maxWidth: '800px',
                margin: '0 auto'
              }}>
                <button 
                  onClick={() => {
                    localStorage.setItem('q6-replacesystem', 'Yes');
                    // If Yes, go to question 7
                    setFormStep(7);
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = '#2a3d66';
                    e.currentTarget.style.transform = 'scale(1.02)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = '#141D46';
                    e.currentTarget.style.transform = 'scale(1)';
                  }}
                  style={{
                    padding: '25px 20px',
                    fontSize: '20px',
                    fontWeight: '600',
                    color: '#fff',
                    backgroundColor: '#141D46',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    transition: 'all 0.3s',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '15px',
                    boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
                  }}>
                  <span style={{ fontSize: '30px' }}>✅</span>
                  Yes
                </button>
                
                <button 
                  onClick={() => {
                    localStorage.setItem('q6-replacesystem', 'Maybe');
                    // If Maybe, go to question 7
                    setFormStep(7);
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = '#2a3d66';
                    e.currentTarget.style.transform = 'scale(1.02)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = '#141D46';
                    e.currentTarget.style.transform = 'scale(1)';
                  }}
                  style={{
                    padding: '25px 20px',
                    fontSize: '20px',
                    fontWeight: '600',
                    color: '#fff',
                    backgroundColor: '#141D46',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    transition: 'all 0.3s',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '15px',
                    boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
                  }}>
                  <span style={{ fontSize: '30px' }}>❓</span>
                  Maybe
                </button>
                
                <button 
                  onClick={() => {
                    localStorage.setItem('q6-replacesystem', 'No');
                    // If No, go to next step
                    setFormStep(7);
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = '#2a3d66';
                    e.currentTarget.style.transform = 'scale(1.02)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = '#141D46';
                    e.currentTarget.style.transform = 'scale(1)';
                  }}
                  style={{
                    padding: '25px 20px',
                    fontSize: '20px',
                    fontWeight: '600',
                    color: '#fff',
                    backgroundColor: '#141D46',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    transition: 'all 0.3s',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '15px',
                    boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
                  }}>
                  <span style={{ fontSize: '30px' }}>❌</span>
                  No
                </button>
              </div>
            </>
          )}
          
          {/* Question 7 - How high is your quarterly electricity bill? */}
          {formStep === 7 && (
            <>
              <h2 style={{
                textAlign: 'center',
                fontSize: isMobile ? '28px' : '36px',
                fontWeight: 'bold',
                color: '#141D46',
                marginBottom: '15px',
                lineHeight: '1.3'
              }}>
                How high is your quarterly electricity bill?
              </h2>
              
              <p style={{
                textAlign: 'center',
                fontSize: isMobile ? '14px' : '16px',
                color: '#666',
                marginBottom: '40px'
              }}>
                Your best guess is ok
              </p>
              
              <div style={{
                display: 'grid',
                gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)',
                gap: isMobile ? '20px' : '30px',
                maxWidth: '800px',
                margin: '0 auto'
              }}>
                <button 
                  onClick={() => {
                    localStorage.setItem('q7-electricitybill', '$250-$750');
                    // All answers go to question 8
                    setFormStep(8);
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = '#2a3d66';
                    e.currentTarget.style.transform = 'scale(1.02)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = '#141D46';
                    e.currentTarget.style.transform = 'scale(1)';
                  }}
                  style={{
                    padding: '25px 20px',
                    fontSize: '20px',
                    fontWeight: '600',
                    color: '#fff',
                    backgroundColor: '#141D46',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    transition: 'all 0.3s',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '15px',
                    flexDirection: 'column',
                    boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
                  }}>
                  <span style={{ fontSize: '40px' }}>💵</span>
                  $250-$750
                </button>
                
                <button 
                  onClick={() => {
                    localStorage.setItem('q7-electricitybill', '$750-$899');
                    // All answers go to question 8
                    setFormStep(8);
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = '#2a3d66';
                    e.currentTarget.style.transform = 'scale(1.02)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = '#141D46';
                    e.currentTarget.style.transform = 'scale(1)';
                  }}
                  style={{
                    padding: '25px 20px',
                    fontSize: '20px',
                    fontWeight: '600',
                    color: '#fff',
                    backgroundColor: '#141D46',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    transition: 'all 0.3s',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '15px',
                    flexDirection: 'column',
                    boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
                  }}>
                  <span style={{ fontSize: '40px' }}>💵</span>
                  $750-$899
                </button>
                
                <button 
                  onClick={() => {
                    localStorage.setItem('q7-electricitybill', '$900+');
                    // All answers go to question 8
                    setFormStep(8);
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = '#2a3d66';
                    e.currentTarget.style.transform = 'scale(1.02)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = '#141D46';
                    e.currentTarget.style.transform = 'scale(1)';
                  }}
                  style={{
                    padding: '25px 20px',
                    fontSize: '20px',
                    fontWeight: '600',
                    color: '#fff',
                    backgroundColor: '#141D46',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    transition: 'all 0.3s',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '15px',
                    flexDirection: 'column',
                    boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
                  }}>
                  <span style={{ fontSize: '40px' }}>💵</span>
                  $900+
                </button>
              </div>
            </>
          )}
          
          {/* Question 8 - What's the address of your property? */}
          {formStep === 8 && (
            <>
              <h2 style={{
                textAlign: 'center',
                fontSize: isMobile ? '28px' : '36px',
                fontWeight: 'bold',
                color: '#141D46',
                marginBottom: '15px',
                lineHeight: '1.3'
              }}>
                What's the address of your property?
              </h2>
              
              <p style={{
                textAlign: 'center',
                fontSize: isMobile ? '14px' : '16px',
                color: '#666',
                marginBottom: '40px'
              }}>
                This is required to provide accurate comparison & savings results.
              </p>
              
              <div style={{
                maxWidth: '500px',
                margin: '0 auto'
              }}>
                <input
                  type="text"
                  placeholder="Enter your street address"
                  onChange={(e) => {
                    // Save as they type
                    localStorage.setItem('q8-propertyaddress', e.target.value);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      const address = (e.target as HTMLInputElement).value.trim();
                      if (address !== '') {
                        // Show validation animation
                        setFormStep(8.5);
                      }
                    }
                  }}
                  style={{
                    width: '100%',
                    padding: '15px',
                    fontSize: '16px',
                    marginBottom: '20px',
                    border: '2px solid #e2e8f0',
                    borderRadius: '4px',
                    outline: 'none',
                    transition: 'border-color 0.3s',
                    boxSizing: 'border-box'
                  }}
                  onFocus={(e) => {
                    e.currentTarget.style.borderColor = '#141D46';
                  }}
                  onBlur={(e) => {
                    e.currentTarget.style.borderColor = '#e2e8f0';
                  }}
                />
                <button
                  onClick={() => {
                    const input = document.querySelector('input[placeholder="Enter your street address"]') as HTMLInputElement;
                    if (input && input.value.trim() !== '') {
                      localStorage.setItem('q8-propertyaddress', input.value);
                      // Show validation animation
                      setFormStep(8.5);
                    }
                  }}
                  style={{
                    width: '100%',
                    padding: '18px',
                    fontSize: '20px',
                    fontWeight: 'bold',
                    color: '#fff',
                    backgroundColor: '#141D46',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    transition: 'background-color 0.3s'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = '#1a8fc9';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = '#141D46';
                  }}
                >
                  Continue
                </button>
              </div>
            </>
          )}
          
          {/* Validation Animation */}
          {formStep === 8.5 && (
            <ValidationAnimation 
              isMobile={isMobile}
              onComplete={() => setFormStep(9)}
            />
          )}
          
          {/* Question 9 - Good news and first name */}
          {formStep === 9 && (
            <>
              <h2 style={{
                textAlign: 'center',
                fontSize: isMobile ? '28px' : '36px',
                fontWeight: 'bold',
                color: '#141D46',
                marginBottom: '30px',
                lineHeight: '1.3'
              }}>
                Good news. Your address qualifies for the rebate program.
              </h2>
              
              <h3 style={{
                textAlign: 'center',
                fontSize: isMobile ? '24px' : '28px',
                fontWeight: 'bold',
                color: '#141D46',
                marginBottom: '30px'
              }}>
                What's your first name?
              </h3>
              
              <div style={{
                maxWidth: '500px',
                margin: '0 auto'
              }}>
                <input
                  type="text"
                  placeholder="First name"
                  onChange={(e) => {
                    localStorage.setItem('q9-firstname', e.target.value);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      const name = (e.target as HTMLInputElement).value.trim();
                      if (name !== '') {
                        setFormStep(10);
                      }
                    }
                  }}
                  style={{
                    width: '100%',
                    padding: '15px',
                    fontSize: '16px',
                    marginBottom: '20px',
                    border: '2px solid #e2e8f0',
                    borderRadius: '4px',
                    outline: 'none',
                    transition: 'border-color 0.3s',
                    boxSizing: 'border-box'
                  }}
                  onFocus={(e) => {
                    e.currentTarget.style.borderColor = '#141D46';
                  }}
                  onBlur={(e) => {
                    e.currentTarget.style.borderColor = '#e2e8f0';
                  }}
                />
                <button
                  onClick={() => {
                    const input = document.querySelector('input[placeholder="First name"]') as HTMLInputElement;
                    if (input && input.value.trim() !== '') {
                      localStorage.setItem('q9-firstname', input.value);
                      setFormStep(10);
                    }
                  }}
                  style={{
                    width: '100%',
                    padding: '18px',
                    fontSize: '20px',
                    fontWeight: 'bold',
                    color: '#fff',
                    backgroundColor: '#141D46',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    transition: 'background-color 0.3s'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = '#1a8fc9';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = '#141D46';
                  }}
                >
                  Continue
                </button>
              </div>
            </>
          )}
          
          {/* Question 10 - Last name */}
          {formStep === 10 && (
            <>
              <h2 style={{
                textAlign: 'center',
                fontSize: isMobile ? '28px' : '36px',
                fontWeight: 'bold',
                color: '#141D46',
                marginBottom: '30px',
                lineHeight: '1.3'
              }}>
                What's your last name?
              </h2>
              
              <div style={{
                maxWidth: '500px',
                margin: '0 auto'
              }}>
                <input
                  type="text"
                  placeholder="Last name"
                  onChange={(e) => {
                    localStorage.setItem('q10-lastname', e.target.value);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      const name = (e.target as HTMLInputElement).value.trim();
                      if (name !== '') {
                        setFormStep(11);
                      }
                    }
                  }}
                  style={{
                    width: '100%',
                    padding: '15px',
                    fontSize: '16px',
                    marginBottom: '20px',
                    border: '2px solid #e2e8f0',
                    borderRadius: '4px',
                    outline: 'none',
                    transition: 'border-color 0.3s',
                    boxSizing: 'border-box'
                  }}
                  onFocus={(e) => {
                    e.currentTarget.style.borderColor = '#141D46';
                  }}
                  onBlur={(e) => {
                    e.currentTarget.style.borderColor = '#e2e8f0';
                  }}
                />
                <button
                  onClick={() => {
                    const input = document.querySelector('input[placeholder="Last name"]') as HTMLInputElement;
                    if (input && input.value.trim() !== '') {
                      localStorage.setItem('q10-lastname', input.value);
                      setFormStep(11);
                    }
                  }}
                  style={{
                    width: '100%',
                    padding: '18px',
                    fontSize: '20px',
                    fontWeight: 'bold',
                    color: '#fff',
                    backgroundColor: '#141D46',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    transition: 'background-color 0.3s'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = '#1a8fc9';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = '#141D46';
                  }}
                >
                  Continue
                </button>
              </div>
            </>
          )}
          
          {/* Question 11 - Email address */}
          {formStep === 11 && (() => {
            const firstName = localStorage.getItem('q9-firstname') || '';
            
            return (
              <>
                <h2 style={{
                  textAlign: 'center',
                  fontSize: isMobile ? '28px' : '36px',
                  fontWeight: 'bold',
                  color: '#141D46',
                  marginBottom: '30px',
                  lineHeight: '1.3'
                }}>
                  What's your best email{firstName ? `, ${firstName}` : ''}?
                </h2>
                
                <div style={{
                  maxWidth: '500px',
                  margin: '0 auto'
                }}>
                  <input
                    type="email"
                    placeholder="Email address"
                    onChange={(e) => {
                      localStorage.setItem('q11-email', e.target.value);
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        const email = (e.target as HTMLInputElement).value.trim();
                        if (email !== '' && email.includes('@')) {
                          setFormStep(12);
                        }
                      }
                    }}
                    style={{
                      width: '100%',
                      padding: '15px',
                      fontSize: '16px',
                      marginBottom: '20px',
                      border: '2px solid #e2e8f0',
                      borderRadius: '4px',
                      outline: 'none',
                      transition: 'border-color 0.3s',
                      boxSizing: 'border-box'
                    }}
                    onFocus={(e) => {
                      e.currentTarget.style.borderColor = '#141D46';
                    }}
                    onBlur={(e) => {
                      e.currentTarget.style.borderColor = '#e2e8f0';
                    }}
                  />
                  <button
                    onClick={() => {
                      const input = document.querySelector('input[type="email"]') as HTMLInputElement;
                      if (input && input.value.trim() !== '' && input.value.includes('@')) {
                        localStorage.setItem('q11-email', input.value);
                        setFormStep(12);
                      }
                    }}
                    style={{
                      width: '100%',
                      padding: '18px',
                      fontSize: '20px',
                      fontWeight: 'bold',
                      color: '#fff',
                      backgroundColor: '#141D46',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      transition: 'background-color 0.3s'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = '#1a8fc9';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = '#141D46';
                    }}
                  >
                    Continue
                  </button>
                </div>
              </>
            );
          })()}
          
          {/* Question 12 - Mobile number (Last step) */}
          {formStep === 12 && (() => {
            const firstName = localStorage.getItem('q9-firstname') || '';
            
            return (
              <>
                <h2 style={{
                  textAlign: 'center',
                  fontSize: isMobile ? '28px' : '36px',
                  fontWeight: 'bold',
                  color: '#141D46',
                  marginBottom: '15px',
                  lineHeight: '1.3'
                }}>
                  Last step{firstName ? `, ${firstName}` : ''}!
                </h2>
                
                <h3 style={{
                  textAlign: 'center',
                  fontSize: isMobile ? '22px' : '26px',
                  fontWeight: '600',
                  color: '#333',
                  marginBottom: '15px'
                }}>
                  What's your mobile number?
                </h3>
                
                <p style={{
                  textAlign: 'center',
                  fontSize: isMobile ? '14px' : '16px',
                  color: '#666',
                  marginBottom: '30px'
                }}>
                  We'll text you your personalized solar savings report and rebate information
                </p>
                
                <div style={{
                  maxWidth: '500px',
                  margin: '0 auto'
                }}>
                  <input
                    type="tel"
                    placeholder="Mobile number"
                    onChange={(e) => {
                      localStorage.setItem('q12-mobile', e.target.value);
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        const mobile = (e.target as HTMLInputElement).value.trim();
                        if (mobile !== '' && mobile.length >= 10) {
                          localStorage.setItem('q12-mobile', mobile);
                          // Redirect to thank you page - TEMPORARILY DISABLED FOR DEBUGGING
                          // window.location.href = '/thankyou';
                          console.log('🎯 REDIRECT DISABLED - Check console for Pipedrive logs above');
                          console.log('📋 To re-enable redirect, uncomment line 2995');
                        }
                      }
                    }}
                    style={{
                      width: '100%',
                      padding: '15px',
                      fontSize: '16px',
                      marginBottom: '20px',
                      border: '2px solid #e2e8f0',
                      borderRadius: '4px',
                      outline: 'none',
                      transition: 'border-color 0.3s',
                      boxSizing: 'border-box'
                    }}
                    onFocus={(e) => {
                      e.currentTarget.style.borderColor = '#141D46';
                    }}
                    onBlur={(e) => {
                      e.currentTarget.style.borderColor = '#e2e8f0';
                    }}
                  />
                  <button
                    onClick={async () => {
                      const input = document.querySelector('input[type="tel"]') as HTMLInputElement;
                      if (input && input.value.trim() !== '' && input.value.length >= 10) {
                        localStorage.setItem('q12-mobile', input.value);

                        // Gather all form data from localStorage
                        const firstName = localStorage.getItem('q9-firstname') || '';
                        const lastName = localStorage.getItem('q10-lastname') || '';  // Note: was q9-lastname
                        const email = localStorage.getItem('q11-email') || '';  // Note: was q10-email
                        const phone = input.value;

                        // Map the actual localStorage keys to readable names
                        const formData = {
                          propertyOwnership: localStorage.getItem('q1-doyouownyourownproperty') || '',
                          hasSolarPanels: localStorage.getItem('q3-solarpanelsinstalled') || '',
                          electricityBill: localStorage.getItem('q7-electricitybill') || '',
                          address: localStorage.getItem('q8-propertyaddress') || '',
                          // These seem to be from a different form (property investor form)
                          livingSituation: localStorage.getItem('livingSituation') || '',
                          postcode: localStorage.getItem('postcodeOptions') || '',
                          householdIncome: localStorage.getItem('householdIncome') || '',
                          superBalance: localStorage.getItem('superBalance') || '',
                          yourAge: localStorage.getItem('yourAge') || '',
                          bestDescribes: localStorage.getItem('bestDescribes') || ''
                        };

                        // Debug log to see what's in localStorage
                        console.log('📦 Form Data from localStorage:', formData);
                        console.log('🔍 All localStorage keys:', Object.keys(localStorage));
                        console.log('📋 Full localStorage contents:', {
                          q1: localStorage.getItem('q1-property'),
                          q2: localStorage.getItem('q2-property-type'),
                          q3: localStorage.getItem('q3-postcode'),
                          q4: localStorage.getItem('q4-electricity-bill'),
                          q5: localStorage.getItem('q5-roof-shade'),
                          q6: localStorage.getItem('q6-energy-usage'),
                          q7: localStorage.getItem('q7-install-timing'),
                          q8: localStorage.getItem('q8-address'),
                          q9_first: localStorage.getItem('q9-firstname'),
                          q9_last: localStorage.getItem('q9-lastname'),
                          q10: localStorage.getItem('q10-email'),
                          q12: localStorage.getItem('q12-mobile')
                        });

                        // Create lead in Pipedrive
                        console.log('=== 🚀 PIPEDRIVE INTEGRATION START ===');
                        console.log('📅 Timestamp:', new Date().toISOString());
                        console.log('🌐 Environment:', window.location.href);

                        try {
                          const apiKey = '74c8e81114eb1ab5e825479acc8739360fcedf47';
                          const leadTitle = `${firstName} ${lastName} - Save.OneSolarAustralia.com.au - Lead`;

                          console.log('🔑 Using API Key:', apiKey.substring(0, 10) + '...');
                          console.log('📝 Lead Title:', leadTitle);
                          console.log('👤 User Data:', {
                            firstName,
                            lastName,
                            email,
                            phone
                          });

                          // Quick API key validation test
                          console.log('🧪 Testing API key validity...');
                          const testResponse = await fetch('https://oneproperty.pipedrive.com/api/v1/users/me?api_token=' + apiKey);
                          if (testResponse.ok) {
                            const testData = await testResponse.json();
                            console.log('✅ API Key is valid! Connected as:', testData.data?.name || 'Unknown user');
                          } else {
                            console.error('❌ API Key test failed! Status:', testResponse.status);
                            console.error('This API key may be invalid or expired.');
                          }

                          // Store note content for later use - solar incentives focus
                          const noteContent = `
                            Website: save.onesolaraustralia.com.au

                            === CONTACT DETAILS ===
                            Name: ${firstName} ${lastName}
                            Email: ${email || 'Not provided'}
                            Phone: ${phone}

                            === SOLAR FORM DATA ===
                            Property Ownership: ${formData.propertyOwnership || 'Not answered'}
                            Has Solar Panels: ${formData.hasSolarPanels || 'Not answered'}
                            Electricity Bill: ${formData.electricityBill || 'Not answered'}
                            Property Address: ${formData.address || 'Not provided'}

                            === ADDITIONAL INFO (if available) ===
                            Living Situation: ${formData.livingSituation || 'N/A'}
                            Postcode: ${formData.postcode || 'N/A'}
                            Household Income: ${formData.householdIncome || 'N/A'}
                            Super Balance: ${formData.superBalance || 'N/A'}
                            Age: ${formData.yourAge || 'N/A'}
                            Main Goal: ${formData.bestDescribes || 'N/A'}

                            Lead Source: Solar Incentives Landing Page
                            Interest: Government solar rebates and incentives
                            Timestamp: ${new Date().toLocaleString('en-AU', { timeZone: 'Australia/Brisbane' })}
                          `;

                          // OneSolarAustralia label ID from Pipedrive
                          const oneSolarAustraliaLabelId = '4781efc0-98f6-11f0-800e-07f852cab840';

                          console.log('🏷️ Using OneSolarAustralia label ID:', oneSolarAustraliaLabelId);

                          // STEP 1: Create a person
                          const personData = {
                            name: `${firstName} ${lastName}`,
                            email: email ? [{
                              value: email,
                              primary: true,
                              label: 'work'
                            }] : [],
                            phone: phone ? [{
                              value: phone,
                              primary: true,
                              label: 'mobile'
                            }] : [],
                            visible_to: '3'
                          };

                          console.log('📤 STEP 1: Creating Person...');
                          console.log('Person payload:', JSON.stringify(personData, null, 2));

                          const personUrl = 'https://oneproperty.pipedrive.com/api/v1/persons?api_token=' + apiKey;
                          console.log('🔗 Person API URL:', personUrl.replace(apiKey, apiKey.substring(0, 10) + '...'));

                          const personResponse = await fetch(personUrl, {
                            method: 'POST',
                            headers: {
                              'Content-Type': 'application/json',
                              'Accept': 'application/json'
                            },
                            body: JSON.stringify(personData)
                          });

                          console.log('📥 Person Response Status:', personResponse.status, personResponse.statusText);

                          if (!personResponse.ok) {
                            console.error('❌ Person API call failed with status:', personResponse.status);
                          }

                          const personResult = await personResponse.json();
                          console.log('📥 Person Creation Full Response:', JSON.stringify(personResult, null, 2));

                          if (!personResult.success) {
                            console.error('❌ Person creation failed!');
                            console.error('Error:', personResult.error);
                            console.error('Error Info:', personResult.error_info);
                          }

                          if (personResult.success) {
                            console.log('✅ Person created successfully!');
                            console.log('🆔 Person ID:', personResult.data.id);

                            // STEP 2: Now create the lead
                            const leadData = {
                              title: leadTitle,
                              person_id: personResult.data.id,
                              label_ids: oneSolarAustraliaLabelId ? [oneSolarAustraliaLabelId] : [],
                              expected_close_date: null,
                              value: {
                                amount: 0,
                                currency: 'AUD'
                              }
                            };

                            console.log('📤 STEP 2: Creating Lead...');
                            console.log('Lead payload:', JSON.stringify(leadData, null, 2));

                            const leadResponse = await fetch('https://oneproperty.pipedrive.com/api/v1/leads?api_token=' + apiKey, {
                              method: 'POST',
                              headers: {
                                'Content-Type': 'application/json',
                                'Accept': 'application/json'
                              },
                              body: JSON.stringify(leadData)
                            });

                            console.log('📥 Lead Response Status:', leadResponse.status, leadResponse.statusText);

                            if (!leadResponse.ok) {
                              console.error('❌ Lead API call failed with status:', leadResponse.status);
                            }

                            const leadResult = await leadResponse.json();
                            console.log('📥 Lead Creation Full Response:', JSON.stringify(leadResult, null, 2));

                            if (leadResult.success) {
                              console.log('✅ Lead created successfully!');
                              console.log('🆔 Lead ID:', leadResult.data?.id);
                              console.log('🔗 Direct lead link: https://oneproperty.pipedrive.com/leads/inbox/' + leadResult.data?.id);

                              // STEP 3: Add note
                              const noteData = {
                                content: noteContent,
                                lead_id: leadResult.data?.id
                              };

                              const noteResponse = await fetch('https://oneproperty.pipedrive.com/api/v1/notes?api_token=' + apiKey, {
                                method: 'POST',
                                headers: {
                                  'Content-Type': 'application/json',
                                  'Accept': 'application/json'
                                },
                                body: JSON.stringify(noteData)
                              });

                              const noteResult = await noteResponse.json();
                              console.log('📥 Note Creation Response:', noteResult.success ? '✅ Success' : '❌ Failed');

                              console.log('=== ✅ PIPEDRIVE INTEGRATION COMPLETE ===');
                              alert('Form submitted! Check console for Pipedrive logs (F12 → Console tab)');
                            }
                          }
                        } catch (error: any) {
                          console.error('❌ EXCEPTION occurred during Pipedrive API call:');
                          console.error('Error:', error);
                        }

                        console.log('=== 🏁 END OF PIPEDRIVE INTEGRATION ===');

                        // Redirect to thank you page - TEMPORARILY DISABLED FOR DEBUGGING
                        // window.location.href = '/thankyou';
                        console.log('🎯 REDIRECT DISABLED - Check console for Pipedrive logs above');
                        console.log('📋 To re-enable redirect, uncomment line 3024');
                      }
                    }}
                    style={{
                      width: '100%',
                      padding: '18px',
                      fontSize: '20px',
                      fontWeight: 'bold',
                      color: '#fff',
                      backgroundColor: '#00a651',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      transition: 'background-color 0.3s'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = '#008c44';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = '#00a651';
                    }}
                  >
                    Access Your Strategic Session Today!
                  </button>
                </div>
              </>
            );
          })()}
          
          {/* Question 13 - Success/Thank you page */}
          {formStep === 13 && (
            <div style={{
              textAlign: 'center',
              padding: '40px 20px',
              minHeight: '400px'
            }}>
              <div style={{
                fontSize: '60px',
                marginBottom: '20px'
              }}>
                🎉
              </div>
              <h2 style={{
                fontSize: isMobile ? '28px' : '36px',
                fontWeight: 'bold',
                color: '#00a651',
                marginBottom: '20px'
              }}>
                Congratulations!
              </h2>
              <p style={{
                fontSize: isMobile ? '18px' : '20px',
                color: '#333',
                marginBottom: '30px',
                maxWidth: '600px',
                margin: '0 auto 30px'
              }}>
                Your solar incentive assessment is complete. We'll send your personalized report shortly!
              </p>
              <button
                onClick={() => {
                  // Clear all questionnaire data from localStorage
                  localStorage.removeItem('q1-doyouownyourownproperty');
                  localStorage.removeItem('q2-rentersresponse');
                  localStorage.removeItem('q3-solarpanelsinstalled');
                  localStorage.removeItem('q4-systembenefits');
                  localStorage.removeItem('q5-replacesystem');
                  localStorage.removeItem('q6-replacesystem');
                  localStorage.removeItem('q7-electricitybill');
                  localStorage.removeItem('q8-propertyaddress');
                  localStorage.removeItem('q9-firstname');
                  localStorage.removeItem('q10-lastname');
                  localStorage.removeItem('q11-email');
                  localStorage.removeItem('q12-mobile');
                  // Reset to first question
                  setFormStep(0);
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = '#2a3d66';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = '#141D46';
                }}
                style={{
                  padding: '15px 30px',
                  fontSize: '18px',
                  fontWeight: 'bold',
                  color: '#fff',
                  backgroundColor: '#141D46',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  transition: 'background-color 0.3s'
                }}
              >
                Start Over
              </button>
            </div>
          )}
          
          {/* Review Bottom Section */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '20px',
            gap: '15px'
          }}>
            <img 
              src="/assets/easypropertyinvestorimages/lady-face.png" 
              alt="Avatar" 
              style={{
                width: '60px',
                height: '60px',
                borderRadius: '50%'
              }}
            />
            <img 
              src="/assets/easypropertyinvestorimages/blue-tick.png" 
              alt="check" 
              style={{
                width: '30px',
                height: '30px'
              }}
            />
            <p style={{
              margin: 0,
              fontSize: '16px',
              color: '#666'
            }}>
              This is required to determine eligibility
            </p>
          </div>
          </div>
        </div>
        
        {/* Credibility Badges Section */}
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          margin: '20px 0'
        }}>
          <img 
            src="/assets/onesolarincentives/googlereviews.png"
            alt="Google Reviews"
            style={{
              maxWidth: '100%',
              height: 'auto'
            }}
          />
        </div>
        
        <p style={{
          color: '#000000',
          fontSize: isMobile ? '24px' : '36px',
          fontFamily: 'Gilroy',
          fontWeight: 'bold',
          margin: isMobile ? '30px 0 15px 0' : '40px 0 20px 0',
          textAlign: 'center',
          padding: isMobile ? '0 10px' : '0'
        }}>
          Why One Solar?
        </p>
        
        {/* Three Column Features Grid */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)',
          gap: '30px',
          maxWidth: '1200px',
          margin: '20px auto 0',
          padding: '0 20px'
        }}>
          {/* Access Solar Incentives */}
          <div style={{
            textAlign: 'center',
            padding: '20px'
          }}>
            {/* Placeholder for image */}
            <div style={{
              width: '80px',
              height: '80px',
              backgroundColor: '#f0f0f0',
              borderRadius: '50%',
              margin: '0 auto 20px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '40px'
            }}>
              💰
            </div>
            <h3 style={{
              fontSize: isMobile ? '18px' : '20px',
              fontWeight: 'bold',
              color: '#141D46',
              marginBottom: '15px'
            }}>
              Access Solar & Battery Incentives
            </h3>
            <p style={{
              fontSize: isMobile ? '14px' : '16px',
              color: '#666',
              lineHeight: '1.6'
            }}>
              In less than 30-seconds, you'll find out if you qualify for Government Solar & Battery Incentives for up to $18,600 for batteries and $5,350 for solar which are reducing again in 2026.
            </p>
          </div>
          
          {/* In home - cost free analysis */}
          <div style={{
            textAlign: 'center',
            padding: '20px'
          }}>
            <div style={{
              width: '80px',
              height: '80px',
              backgroundColor: '#f0f0f0',
              borderRadius: '50%',
              margin: '0 auto 20px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '40px'
            }}>
              🏠
            </div>
            <h3 style={{
              fontSize: isMobile ? '18px' : '20px',
              fontWeight: 'bold',
              color: '#141D46',
              marginBottom: '15px'
            }}>
              In-Home Cost-Free Analysis
            </h3>
            <p style={{
              fontSize: isMobile ? '14px' : '16px',
              color: '#666',
              lineHeight: '1.6'
            }}>
              Due diligence with comprehensive in-home analysis at no cost.
            </p>
          </div>

          {/* Over 10,000 installs in SEQ */}
          <div style={{
            textAlign: 'center',
            padding: '20px'
          }}>
            <div style={{
              width: '80px',
              height: '80px',
              backgroundColor: '#f0f0f0',
              borderRadius: '50%',
              margin: '0 auto 20px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '40px'
            }}>
              ⚡
            </div>
            <h3 style={{
              fontSize: isMobile ? '18px' : '20px',
              fontWeight: 'bold',
              color: '#141D46',
              marginBottom: '15px'
            }}>
              Over 10,000 Installs in SEQ
            </h3>
            <p style={{
              fontSize: isMobile ? '14px' : '16px',
              color: '#666',
              lineHeight: '1.6'
            }}>
              Proven track record with thousands of successful installations.
            </p>
          </div>

          {/* In business for over 7 years */}
          <div style={{
            textAlign: 'center',
            padding: '20px'
          }}>
            <div style={{
              width: '80px',
              height: '80px',
              backgroundColor: '#f0f0f0',
              borderRadius: '50%',
              margin: '0 auto 20px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '40px'
            }}>
              ✓
            </div>
            <h3 style={{
              fontSize: isMobile ? '18px' : '20px',
              fontWeight: 'bold',
              color: '#141D46',
              marginBottom: '15px'
            }}>
              In Business for Over 7 Years
            </h3>
            <p style={{
              fontSize: isMobile ? '14px' : '16px',
              color: '#666',
              lineHeight: '1.6'
            }}>
              Established and trusted solar provider with years of experience.
            </p>
          </div>

          {/* Access to premium panels */}
          <div style={{
            textAlign: 'center',
            padding: '20px'
          }}>
            <div style={{
              width: '80px',
              height: '80px',
              backgroundColor: '#f0f0f0',
              borderRadius: '50%',
              margin: '0 auto 20px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '40px'
            }}>
              ☀️
            </div>
            <h3 style={{
              fontSize: isMobile ? '18px' : '20px',
              fontWeight: 'bold',
              color: '#141D46',
              marginBottom: '15px'
            }}>
              Premium Panels with Low Degradation
            </h3>
            <p style={{
              fontSize: isMobile ? '14px' : '16px',
              color: '#666',
              lineHeight: '1.6'
            }}>
              Access to high-quality panels that maintain efficiency over time.
            </p>
          </div>

          {/* Member of Masters Electrician */}
          <div style={{
            textAlign: 'center',
            padding: '20px'
          }}>
            <div style={{
              width: '80px',
              height: '80px',
              backgroundColor: '#f0f0f0',
              borderRadius: '50%',
              margin: '0 auto 20px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '40px'
            }}>
              🔧
            </div>
            <h3 style={{
              fontSize: isMobile ? '18px' : '20px',
              fontWeight: 'bold',
              color: '#141D46',
              marginBottom: '15px'
            }}>
              Member of Master Electricians
            </h3>
            <p style={{
              fontSize: isMobile ? '14px' : '16px',
              color: '#666',
              lineHeight: '1.6'
            }}>
              Certified professionals ensuring quality and safety standards.
            </p>
          </div>

          {/* Quality conscious in-house electricians */}
          <div style={{
            textAlign: 'center',
            padding: '20px'
          }}>
            <div style={{
              width: '80px',
              height: '80px',
              backgroundColor: '#f0f0f0',
              borderRadius: '50%',
              margin: '0 auto 20px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '40px'
            }}>
              ⚡
            </div>
            <h3 style={{
              fontSize: isMobile ? '18px' : '20px',
              fontWeight: 'bold',
              color: '#141D46',
              marginBottom: '15px'
            }}>
              Quality In-House Electricians
            </h3>
            <p style={{
              fontSize: isMobile ? '14px' : '16px',
              color: '#666',
              lineHeight: '1.6'
            }}>
              We take the risk out of the install, using quality conscious in-house electricians.
            </p>
          </div>

          {/* Fast installation */}
          <div style={{
            textAlign: 'center',
            padding: '20px'
          }}>
            <div style={{
              width: '80px',
              height: '80px',
              backgroundColor: '#f0f0f0',
              borderRadius: '50%',
              margin: '0 auto 20px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '40px'
            }}>
              🚀
            </div>
            <h3 style={{
              fontSize: isMobile ? '18px' : '20px',
              fontWeight: 'bold',
              color: '#141D46',
              marginBottom: '15px'
            }}>
              Install Done in Two Weeks
            </h3>
            <p style={{
              fontSize: isMobile ? '14px' : '16px',
              color: '#666',
              lineHeight: '1.6'
            }}>
              We can get an install done in two weeks, don't wait 3 months to lower your bills! Our sparkies actually show up on time!
            </p>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer style={{
        backgroundColor: '#fff',
        marginTop: '10px'
      }}>
        <div style={{
          borderTop: '1px solid #e2e8f0',
          padding: '40px 20px',
          maxWidth: '1200px',
          margin: '0 auto'
        }}>
          <div style={{
            display: 'flex',
            gap: isMobile ? '15px' : '30px',
            justifyContent: 'center',
            marginBottom: isMobile ? '20px' : '30px',
            flexWrap: 'wrap',
            flexDirection: isMobile ? 'column' : 'row',
            alignItems: 'center'
          }}>
            <a href="/privacy-policy" style={{
              color: '#666',
              fontSize: isMobile ? '14px' : '18px',
              fontWeight: 'bold',
              textDecoration: 'none'
            }}>Privacy Policy</a>
            <a href="/terms-and-conditions" style={{
              color: '#666',
              fontSize: isMobile ? '14px' : '18px',
              fontWeight: 'bold',
              textDecoration: 'none'
            }}>Terms and Conditions</a>
          </div>
          
          <div style={{
            color: '#666',
            fontSize: isMobile ? '10px' : '12px',
            lineHeight: '1.6',
            textAlign: 'justify',
            margin: '0 auto',
            padding: isMobile ? '0 10px' : '0'
          }}>
            <p style={{ marginBottom: '20px' }}>
              One Solar Australia is part of the One Group Australasia Pty Ltd (ACN: 642 690 435) family of companies, including One Finance Australia and One Property Australia. Together, we are committed to delivering innovative and tailored solutions across mortgage and asset finance, property acquisition and solar installation.
            </p>
            <p>
              The website may share your details with independent property and real estate consultants who may contact you to provide the services you have requested. You are not obligated to accept or engage with any of these services. By submitting your information through <a href="https://onesolaraustralia.com.au" style={{ color: '#a7a7a7', fontWeight: 'bold', textDecoration: 'none' }}>onesolaraustralia.com.au</a>, you consent to us providing your details to third parties and to being contacted by phone, SMS, or email regarding your enquiry. Please review our Privacy Policy for more details on how we manage and protect your information.
            </p>
            <p style={{ marginTop: '10px' }}>
              We do not provide financial product advice or financial dealing services, nor do we endorse financial products or issuers. Our role is to connect your enquiry with a third-party provider that may be able to offer comparisons across a variety of products, services, and providers. Before engaging any provider, you should carefully assess whether their services are appropriate for your financial situation, needs, and goals, and review their disclosure documents (such as their financial services guide). If you choose to proceed with a service offered after being referred via <a href="https://onesolaraustralia.com.au" style={{ color: '#a7a7a7', fontWeight: 'bold', textDecoration: 'none' }}>onesolaraustralia.com.au</a>, your dealings will be directly with that provider and not with One Solar Australia Pty Ltd.
            </p>
            <p style={{ marginTop: '10px' }}>
              We are not involved in delivering financial advice and take no responsibility for the services provided by third-party providers. To the fullest extent permitted by law, we are not liable for any loss or damage arising from your reliance on or use of services delivered by external providers. We also make no representation as to the quality, accuracy, or suitability of any financial advice offered by these third parties.
            </p>
            <p style={{ marginTop: '10px' }}>
              One Solar Australia Pty Ltd may receive a fixed referral fee when your details are passed on to relevant providers. This fee is not commission-based, is not dependent on the outcome of your inquiry, and does not vary based on any investment decisions you may make. While we aim to ensure all information presented on our website is current and accurate, there may be times when updates are required and some details may be temporarily out of date.
            </p>
          </div>
          
          <p style={{
            color: '#666',
            fontSize: '14px',
            margin: '30px 0 0 0',
            textAlign: 'center'
          }}>
            © 2025 One Solar Australia. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default OneSolarIncentivesLandingPage;